import { Component } from '@nicolas-riciotti/light2';

/**
 * <manual-chapternav> — chapter-level controller for the Manual template.
 *
 *  - Updates the bottom pill (current chapter number + title) on scroll.
 *  - Marks the current chapter (is-current) so its sub-dots expand.
 *  - Runs a staggered SPRING cascade on the sub-dots when a chapter expands /
 *    collapses (Newton's-cradle-ish momentum down the line). JS owns the dots'
 *    transform/opacity; CSS only handles the container's height collapse.
 *  - Auto-hides the pill on scroll-down, reveals on scroll-up / touch.
 *
 * Sub-topic dot highlighting inside each group is handled by <block-nav>.
 */
export default class ManualChapterNav extends Component {
  attached() {
    this.chapters = Array.from(document.querySelectorAll('[data-manual-chapter]'));
    this.rails = Array.from(document.querySelectorAll('[data-manual-rail]'));

    this.$label = this.querySelector('[data-manual-current-label]');
    this.$title = this.querySelector('[data-manual-current-title]');
    this.$prev = this.querySelector('[data-manual-prev]');
    this.$next = this.querySelector('[data-manual-next]');

    if (!this.chapters.length) return;

    // Per-chapter sub-dot items + their containers (for the spring cascade).
    this.railItems = this.rails.map((rail) => Array.from(rail.querySelectorAll('.manual__subdot-item')));
    this.railGroups = this.rails.map((rail) => rail.querySelector('.manual__subdots'));
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // JS owns the dots — start them all hidden.
    this.railItems.forEach((items) => items.forEach((el) => this.setItem(el, this.reduced ? 1 : 0)));

    this.current = -1;
    this.currentSection = -1;
    // Mobile: prev/next step through SECTIONS. Desktop: through CHAPTERS.
    this.isMobile = window.innerWidth <= 600;
    if (this.isMobile) {
      this._onPrev = () => this.navigateToSection(this.currentSection - 1);
      this._onNext = () => this.navigateToSection(this.currentSection + 1);
    } else {
      this._onPrev = () => this.navigateTo(this.current - 1);
      this._onNext = () => this.navigateTo(this.current + 1);
    }
    this.$prev?.addEventListener('click', this._onPrev);
    this.$next?.addEventListener('click', this._onNext);

    this.onIntersect = this.onIntersect.bind(this);
    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: '-40% 0px -55% 0px',
      threshold: 0,
    });
    this.chapters.forEach((chapter) => this.observer.observe(chapter));

    // Mobile: track the current SECTION (sub-topic) so the bottom nav can step
    // through sections — chapter line stays, section name below changes.
    if (this.isMobile) {
      this.sections = Array.from(document.querySelectorAll('[data-manual-topic]'));
      this.sectionObserver = new IntersectionObserver((entries) => {
        if (this._navLock) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = this.sections.indexOf(entry.target);
          if (i !== -1) this.setCurrentSection(i);
        });
      }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      this.sections.forEach((s) => this.sectionObserver.observe(s));
    }

    // Clicking a chapter dot opens that chapter directly (and scrolls to it),
    // instead of relying on the scroll-spy catching it mid-scroll.
    this._chapterDotHandlers = [];
    this.rails.forEach((rail) => {
      const idx = Number(rail.getAttribute('data-chapter-index'));
      const dot = rail.querySelector('.manual__chapter-dot');
      if (!dot) return;
      const handler = (e) => { e.preventDefault(); this.navigateTo(idx); };
      dot.addEventListener('click', handler);
      this._chapterDotHandlers.push({ dot, handler });
    });

    // When a sub-topic becomes active (scroll arrival or click), briefly reveal a
    // label, then tuck it back in. For a chapter's FIRST sub-topic, peek the
    // chapter label instead (arriving at .0 ~= arriving at the chapter).
    this.subdotLinks = Array.from(document.querySelectorAll('.manual__subdot'));
    this._peek = (el, duration) => {
      if (!el) return;
      el.classList.add('is-peek');
      clearTimeout(el._peekTimer);
      el._peekTimer = setTimeout(() => el.classList.remove('is-peek'), duration || 1300);
    };
    // Peeks are exclusive: continuous scrolling activates sections in quick
    // succession, and each label lingering on its own timer left a trail of
    // revealed labels for sections already off screen. Clear the rest first.
    this._clearPeeks = () => {
      document.querySelectorAll('.manual__dotnav .is-peek').forEach((el) => {
        el.classList.remove('is-peek');
        clearTimeout(el._peekTimer);
      });
    };
    this._peekObserver = new MutationObserver((mutations) => {
      let cleared = false;
      mutations.forEach((m) => {
        const el = m.target;
        const had = m.oldValue ? m.oldValue.split(/\s+/).indexOf('is-active') !== -1 : false;
        if (had || !el.classList.contains('is-active')) return;
        if (!cleared) { this._clearPeeks(); cleared = true; }
        const rail = el.closest('[data-manual-rail]');
        const isFirst = rail && rail.querySelector('.manual__subdot') === el;
        if (isFirst) {
          this._peek(rail.querySelector('.manual__chapter-dot'), 1300); // chapter lingers
          this._peek(el, 650);                                          // section tucks away faster
        } else {
          this._peek(el, 1300);
        }
      });
    });
    this.subdotLinks.forEach((el) => this._peekObserver.observe(el, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    }));

    // Bottom-pill chapter selector: click the label to expand the full list.
    this.$toggle = this.querySelector('[data-manual-toggle]');
    this.rows = Array.from(this.querySelectorAll('[data-manual-goto]'));
    this._onToggle = (e) => {
      e.stopPropagation();
      this.classList.toggle('is-open');
      if (this.classList.contains('is-open')) this._openedAt = performance.now();
    };
    this.$toggle?.addEventListener('click', this._onToggle);
    this._rowHandlers = [];
    this.rows.forEach((row) => {
      const idx = Number(row.getAttribute('data-manual-goto'));
      const handler = (e) => { e.preventDefault(); this.classList.remove('is-open'); this.navigateTo(idx); };
      row.addEventListener('click', handler);
      this._rowHandlers.push({ row, handler });
    });
    this._onDocClick = (e) => {
      if (this.classList.contains('is-open') && !this.contains(e.target)) this.classList.remove('is-open');
    };
    this._onKey = (e) => { if (e.key === 'Escape') this.classList.remove('is-open'); };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);

    // Auto-hide the pill on scroll down; reveal on scroll up or touch.
    this._lastY = window.scrollY || 0;
    this._onScroll = () => {
      if (this.classList.contains('is-open')) {
        // Don't dismiss the selector the instant the user scrolls — give it a moment.
        if (performance.now() - (this._openedAt || 0) > 900) this.classList.remove('is-open');
        return;
      }
      const y = window.scrollY || 0;
      const delta = y - this._lastY;
      if (Math.abs(delta) < 4) return;
      if (delta > 0 && y > 120 && !this._cursorNearBottom) this.classList.add('is-hidden');
      else if (delta < 0 || this._cursorNearBottom) this.classList.remove('is-hidden');
      this._lastY = y;
    };
    this._onTouch = () => this.classList.remove('is-hidden');
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('touchstart', this._onTouch, { passive: true });

    // The hidden pill is pointer-events-none and translated off screen, so it
    // can't be hovered directly — moving the cursor into the bottom strip of
    // the viewport brings it back (and keeps it from re-hiding while there).
    this._cursorNearBottom = false;
    if (!this.isMobile) {
      this._onMouseMove = (e) => {
        const near = e.clientY > window.innerHeight - 120;
        if (near === this._cursorNearBottom) return;
        this._cursorNearBottom = near;
        if (near) this.classList.remove('is-hidden');
      };
      window.addEventListener('mousemove', this._onMouseMove, { passive: true });
    }

    // Spread the dots when a fly-out label wraps to a second line: grow the ONE
    // shared gap (--manual-rail-gap) by the extra label height, so spacing stays
    // even across the whole rail. Baseline: the 30px chapter pill fits the 16px
    // gap, so gap = 16 + (tallest label − 30), never below 16.
    if (!this.isMobile) {
      this.dotnav = document.querySelector('.manual__dotnav');
      this._sizeRailGap = () => {
        if (!this.dotnav) return;
        let maxH = 0;
        this.dotnav.querySelectorAll('.manual__dot-label').forEach((el) => {
          maxH = Math.max(maxH, el.offsetHeight);
        });
        const gap = Math.max(16, maxH - 14);
        this.dotnav.style.setProperty('--manual-rail-gap', gap + 'px');
        // The open chapter's max-height was measured with the old gap — refresh it.
        const group = this.railGroups[this.current];
        if (group && group.style.maxHeight && group.style.maxHeight !== '0px') {
          group.style.maxHeight = group.scrollHeight + 'px';
        }
      };
      this._sizeRailGap();
      document.fonts?.ready.then(() => this._sizeRailGap?.());
      this._onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(this._sizeRailGap, 150);
      };
      window.addEventListener('resize', this._onResize);
    }

    // Chapter-dot entrance is pure CSS (staggered keyframes). JS only delays the
    // first chapter's expand so it happens AFTER the dots have slid in.
    const introDelay = (this.reduced || this.isMobile) ? 0 : 800;
    this._navLock = true; // hold the scroll-spy during the intro
    setTimeout(() => {
      this._navLock = false;
      this.setCurrent(0);
      if (this.isMobile) this.setCurrentSection(0);
    }, introDelay);
  }

  // Apply a normalized progress p (0 = hidden/up, 1 = resting) to a dot.
  setItem(el, p) {
    const startY = -36; // px the dot drops from
    el.style.opacity = Math.max(0, Math.min(1, p * 1.8));
    el.style.transform = `translateY(${(1 - p) * startY}px) scale(${Math.max(0, p)})`;
  }

  // Staggered spring cascade across one chapter's sub-dots.
  cascade(index, direction) {
    const items = this.railItems[index] || [];
    const group = this.railGroups[index];
    if (!items.length) return;

    // Cancel anything in flight for this chapter.
    items.forEach((el) => {
      if (el._raf) cancelAnimationFrame(el._raf);
      if (el._to) clearTimeout(el._to);
    });
    if (group) {
      if (group._peTimer) clearTimeout(group._peTimer);
      if (group._startTimer) clearTimeout(group._startTimer);
      if (group._onExpandEnd) {
        group.removeEventListener('transitionend', group._onExpandEnd);
        group._onExpandEnd = null;
      }
    }

    if (this.reduced) {
      items.forEach((el) => this.setItem(el, direction === 'in' ? 1 : 0));
      if (group) group.style.maxHeight = direction === 'in' ? group.scrollHeight + 'px' : '0px';
      return;
    }

    const stagger = 90;       // ms between dots — momentum travelling down the line
    const stiffness = 0.058;  // lower = slower / softer glide
    const damping = 0.74;     // velocity retained per frame: lower = settles faster / less bounce
    const target = direction === 'in' ? 1 : 0;

    const runSprings = () => {
      if (group) group.style.pointerEvents = 'none'; // suppress hover labels while dots move
      items.forEach((el, i) => {
        const delay = i * stagger; // top dot first, then downward
        let p = direction === 'in' ? 0 : 1;
        let v = 0;
        const step = () => {
          v = (v + (target - p) * stiffness) * damping;
          p += v;
          const settled = Math.abs(target - p) < 0.002 && Math.abs(v) < 0.002;
          this.setItem(el, settled ? target : p);
          if (!settled) el._raf = requestAnimationFrame(step);
        };
        el._to = setTimeout(() => { el._raf = requestAnimationFrame(step); }, delay);
      });
      const maxDur = (items.length - 1) * stagger + 1800;
      if (group) group._peTimer = setTimeout(() => { group.style.pointerEvents = ''; }, maxDur);
    };

    if (direction === 'in') {
      // Keep dots hidden during the container's expand, then spring them in the
      // moment the expand (max-height transition) finishes — synced, not guessed.
      items.forEach((el) => this.setItem(el, 0));
      if (group) {
        // Open to the exact content height (items keep layout space while hidden).
        group.style.maxHeight = group.scrollHeight + 'px';
        // Fire the dots while the box is still decelerating into place, so the
        // "stop" of the open feels like it flings the dots out — no dead gap.
        const EXPAND_MS = 650;
        group._startTimer = setTimeout(runSprings, EXPAND_MS * 0.65);
      } else {
        runSprings();
      }
    } else {
      if (group) group.style.maxHeight = '0px'; // collapse
      runSprings(); // spring out immediately
    }
  }

  onIntersect(entries) {
    if (this._navLock) return; // ignore mid-scroll intersections during a click/prev-next jump
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.chapters.indexOf(entry.target);
      if (index !== -1) this.setCurrent(index);
    });
  }

  setCurrent(index) {
    if (index === this.current) return;
    const prev = this.current;
    this.current = index;

    const chapter = this.chapters[index];
    if (!chapter) return;

    // On mobile the pill text is driven by the SECTION nav instead.
    if (!this.isMobile) {
      if (this.$label) this.$label.textContent = chapter.getAttribute('data-chapter-label') || '';
      if (this.$title) this.$title.textContent = chapter.getAttribute('data-chapter-title') || '';
    }

    this.rails.forEach((rail) => {
      rail.classList.toggle('is-current', Number(rail.getAttribute('data-chapter-index')) === index);
    });

    this.rows?.forEach((row) => {
      row.classList.toggle('is-active', Number(row.getAttribute('data-manual-goto')) === index);
    });

    if (prev !== -1 && prev !== index) this.cascade(prev, 'out');
    this.cascade(index, 'in');

    if (!this.isMobile) {
      const atStart = index <= 0;
      const atEnd = index >= this.chapters.length - 1;
      this.$prev?.classList.toggle('opacity-30', atStart);
      this.$next?.classList.toggle('opacity-30', atEnd);
      if (this.$prev) this.$prev.disabled = atStart;
      if (this.$next) this.$next.disabled = atEnd;
    }
  }

  // Mobile section nav: keep the chapter line, swap the section name below.
  setCurrentSection(index) {
    if (index === this.currentSection) return;
    this.currentSection = index;
    const section = this.sections[index];
    if (!section) return;
    if (this.$label) this.$label.textContent = section.getAttribute('data-chapter-label') || '';

    const sectionLabel = section.getAttribute('data-section-label') || '';
    clearTimeout(this._sectionPeekTimer);
    if (this.$title) {
      // On a chapter's first section, sneak in the chapter title, then settle on the section name.
      if (section.getAttribute('data-section-first') === '1') {
        this.$title.textContent = section.getAttribute('data-chapter-title') || sectionLabel;
        this._sectionPeekTimer = setTimeout(() => {
          if (this.$title) this.$title.textContent = sectionLabel;
        }, 900);
      } else {
        this.$title.textContent = sectionLabel;
      }
    }

    const atStart = index <= 0;
    const atEnd = index >= this.sections.length - 1;
    this.$prev?.classList.toggle('opacity-30', atStart);
    this.$next?.classList.toggle('opacity-30', atEnd);
    if (this.$prev) this.$prev.disabled = atStart;
    if (this.$next) this.$next.disabled = atEnd;
  }

  navigateToSection(index) {
    if (index < 0 || index >= this.sections.length || index === this.currentSection) return;
    this._navLock = true;
    clearTimeout(this._navLockTimer);
    this.setCurrentSection(index);
    this.sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._navLockTimer = setTimeout(() => { this._navLock = false; this._lastY = window.scrollY || 0; }, 1000);
  }

  navigateTo(index) {
    if (index < 0 || index >= this.chapters.length || index === this.current) return;
    // Set the target chapter immediately (open it / collapse the rest) and lock
    // the scroll-spy so passing chapters don't open/collapse during the scroll.
    this._navLock = true;
    clearTimeout(this._navLockTimer);
    this.setCurrent(index);
    this.chapters[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    this._navLockTimer = setTimeout(() => {
      this._navLock = false;
      this._lastY = window.scrollY || 0;
    }, 1000);
  }

  detached() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    clearTimeout(this._resizeTimer);
    this._sizeRailGap = null;
    this.observer?.disconnect();
    this.sectionObserver?.disconnect();
    this.$prev?.removeEventListener('click', this._onPrev);
    this.$next?.removeEventListener('click', this._onNext);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('touchstart', this._onTouch);
    if (this._onMouseMove) window.removeEventListener('mousemove', this._onMouseMove);
    clearTimeout(this._navLockTimer);
    clearTimeout(this._sectionPeekTimer);
    this._chapterDotHandlers?.forEach(({ dot, handler }) => dot.removeEventListener('click', handler));
    this._peekObserver?.disconnect();
    this.subdotLinks?.forEach((el) => clearTimeout(el._peekTimer));
    this._chapterDotHandlers?.forEach(({ dot }) => clearTimeout(dot._peekTimer));
    this.$toggle?.removeEventListener('click', this._onToggle);
    this._rowHandlers?.forEach(({ row, handler }) => row.removeEventListener('click', handler));
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    this.railItems?.forEach((items) => items.forEach((el) => {
      if (el._raf) cancelAnimationFrame(el._raf);
      if (el._to) clearTimeout(el._to);
    }));
    this.railGroups?.forEach((group) => {
      if (!group) return;
      if (group._peTimer) clearTimeout(group._peTimer);
      if (group._startTimer) clearTimeout(group._startTimer);
      if (group._onExpandEnd) group.removeEventListener('transitionend', group._onExpandEnd);
    });
  }
}

customElements.define('manual-chapternav', ManualChapterNav);
